import React, { useState, useEffect } from "react";
import SEO from "../seo";
const ChangeYourPlan = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "steps", "billing"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Change Your Plan | xPage Help Center"
        description="Learn how to upgrade or downgrade your xPage plan, see steps for changing your subscription, and understand billing after plan changes."
        keywords="xPage, change plan, upgrade, downgrade, billing, subscription, xPage plan"
        canonical="https://help.xpage.ai/help/landing-page-account/Change%20your%20plan"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          Change your plan
        </h1>

        {/* Overview Section */}
        <section id="overview" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Overview
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              You can upgrade or downgrade your plan at any time. Changes take
              effect immediately and are billed on a prorated basis. Once
              changed, you'll instantly gain access to the features of your new
              plan.
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                If your store is on a free trial, changing your plan will not
                shorten the trial period or affect your store data.
              </li>
              <li>
                Only the store owner or staff with permissions can make plan
                changes. See Roles & Permissions for more details.
              </li>
            </ul>
          </div>
        </section>

        {/* Steps Section */}
        <section id="steps" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Steps to change your plan:
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">Log in to the xPage admin.</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Go to Settings then Billing & Subscription.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  In the Subscription section, click Change Plan.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Choose your preferred plan (Starter, xPage, Pro, or
                  Enterprise) and billing cycle (monthly or annual).
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click Subscribe to confirm your change.
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* Billing Section */}
        <section id="billing" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Billing after changing plans
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              When you change your plan, xPage immediately generates a new
              invoice that includes only the cost of your new plan.
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                Immediate billing: A new invoice is generated as soon as the
                plan is changed.
              </li>
              <li>
                Instant activation: Features of the new plan are available right
                away.
              </li>
            </ul>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#steps"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "steps"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Steps to Change Plan
              </a>
            </li>
            <li>
              <a
                href="#billing"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "billing"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Billing After Changes
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ChangeYourPlan;
